import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, ThumbsUp, ThumbsDown, EyeOff, Eye, CornerDownRight } from "lucide-react";
import { API, errMsg, fmtDate, TOPIC_NAMES } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

export default function AdminComments() {
  const navigate = useNavigate();
  const [comments, setComments] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    API.get("/admin/comments").then(({ data }) => setComments(data))
      .catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, []);

  const reply = async (c) => {
    if (!replyBody.trim()) return;
    setBusy(true);
    try {
      await API.post(`/questions/${c.question_id}/comments`, {
        body: replyBody, parent_id: c.parent_id || c._id,
      });
      toast.success("Reply posted");
      setReplyTo(null);
      setReplyBody("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const hide = async (c) => {
    try {
      await API.delete(`/admin/comments/${c._id}`);
      toast.success("Comment hidden");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const restore = async (c) => {
    try {
      await API.post(`/admin/comments/${c._id}/restore`);
      toast.success("Comment restored");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!comments) return <p className="py-16 text-center text-sm text-muted-foreground">Loading comments…</p>;

  return (
    <div data-testid="admin-comments-page" className="max-w-4xl">
      <h1 className="font-display text-2xl font-bold mb-2" data-testid="admin-comments-title">User comments</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Every discussion comment across the question bank. Click a question reference to open that question in the editor.
      </p>
      {comments.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground" data-testid="comments-empty">No comments yet.</p>
      ) : (
        <div className="space-y-4" data-testid="comments-list">
          {comments.map((c) => (
            <div key={c._id} className={`bg-card border rounded-lg p-5 ${c.status === "hidden" ? "border-destructive/30 opacity-60" : "border-border"}`}
              data-testid={`admin-comment-${c._id}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-2">
                {c.parent_id && <span className="flex items-center gap-1"><CornerDownRight className="w-3.5 h-3.5" /> reply</span>}
                <span className="font-semibold text-foreground">{c.is_admin ? "PAMind team" : (c.user_email || "Unknown user")}</span>
                <span>·</span><span>{fmtDate(c.created_at)}</span>
                <span>·</span>
                <button data-testid={`comment-question-link-${c._id}`} onClick={() => navigate(`/admin/questions/${c.question_id}`)}
                  className="font-semibold text-primary hover:underline">
                  {c.question ? `${c.question.qid} · ${TOPIC_NAMES[c.question.topic] || c.question.topic}` : "Deleted question"}
                </button>
                {c.status === "hidden" && <span className="text-destructive font-semibold">hidden</span>}
              </div>
              {c.parent_body && (
                <p className="text-xs text-muted-foreground italic border-l-2 border-border pl-3 mb-2">Replying to: "{c.parent_body}…"</p>
              )}
              {c.question && <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{c.question.stem}</p>}
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{c.body}</p>
              <div className="mt-3 flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1 text-xs text-muted-foreground"><ThumbsUp className="w-3.5 h-3.5" /> {c.likes_count}</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground"><ThumbsDown className="w-3.5 h-3.5" /> {c.dislikes_count}</span>
                {c.question && (
                  <Button variant="outline" size="sm" data-testid={`comment-open-question-${c._id}`}
                    onClick={() => navigate(`/admin/questions/${c.question_id}`)}>
                    Open question
                  </Button>
                )}
                <Button variant="outline" size="sm" data-testid={`comment-reply-btn-${c._id}`}
                  onClick={() => { setReplyTo(replyTo === c._id ? null : c._id); setReplyBody(""); }}>
                  <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Reply as team
                </Button>
                {c.status !== "hidden" ? (
                  <Button variant="ghost" size="sm" data-testid={`comment-hide-${c._id}`} onClick={() => hide(c)}>
                    <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Hide
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" data-testid={`comment-unhide-${c._id}`} onClick={() => restore(c)}>
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> Unhide
                  </Button>
                )}
              </div>
              {replyTo === c._id && (
                <div className="mt-3" data-testid={`admin-reply-box-${c._id}`}>
                  <Textarea rows={2} value={replyBody} onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Reply to this thread as the PAMind team…" className="text-sm"
                    data-testid={`admin-reply-input-${c._id}`} />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" data-testid={`admin-reply-submit-${c._id}`} onClick={() => reply(c)} disabled={busy || !replyBody.trim()}>
                      {busy ? "Posting…" : "Post reply"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setReplyTo(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
